import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ExternalLink, Sparkles, ArrowUpRight } from 'lucide-react';
import { Nav } from '../components/portfolio/Nav';
import { Footer } from '../components/portfolio/Footer';
import { SnapshotModal } from '../components/snapshot/SnapshotModal';
import { useSnapshot } from '../context/SnapshotContext';
import { CASE_STUDIES, FEATURED_SLUGS } from '../lib/caseStudyData';
import { useReveal } from '../hooks/useReveal';

export default function CaseStudy() {
    const { slug } = useParams();
    const cs = CASE_STUDIES[slug];

    if (!cs) {
        return <Navigate to="/" replace />;
    }

    const idx = FEATURED_SLUGS.indexOf(slug);
    const nextSlug = FEATURED_SLUGS[(idx + 1) % FEATURED_SLUGS.length];
    const next = CASE_STUDIES[nextSlug];

    return (
        <div className="min-h-screen text-white" data-testid="case-study-page">
            <Nav />
            <main className="relative pt-24 pb-24 overflow-hidden">
                <div className="absolute inset-0 grid-bg grid-bg-fade opacity-30" />
                <div className="atmosphere opacity-40" />
                <Header cs={cs} />
                <Screenshot cs={cs} />
                <Lede cs={cs} />
                <Sections cs={cs} />
                <ClosingCTA />
                <NextFile next={next} />
            </main>
            <Footer />
            <SnapshotModal />
        </div>
    );
}

function Header({ cs }) {
    return (
        <header className="relative shell pt-12">
            <div className="flex items-center justify-between gap-6 flex-wrap">
                <p
                    data-testid="case-category"
                    className="font-mono text-[11px] uppercase tracking-[0.24em] text-cyan-400 font-medium"
                >
                    {cs.category}
                </p>
                <Link
                    to="/"
                    data-testid="back-to-portfolio"
                    className="btn-link"
                >
                    <ArrowLeft className="w-4 h-4" />
                    All files
                </Link>
            </div>

            <h1
                data-testid="case-title"
                className="mt-10 font-display font-semibold text-[3rem] sm:text-[4.4rem] lg:text-[6rem] leading-[0.96] tracking-[-0.045em]"
            >
                <span className="text-white">{cs.title}</span>
                <span className="text-gradient-cyan">.</span>
            </h1>

            <div
                className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-3 py-4 border-y font-mono text-[10px] uppercase tracking-[0.24em] font-medium"
                style={{ borderColor: 'rgba(255,255,255,0.10)' }}
            >
                <span data-testid="case-status" className="text-cyan-400 inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-dot" />
                    {cs.meta.status}
                </span>
                <span className="text-slate-400">{cs.meta.year}</span>
                <span className="text-slate-400">{cs.meta.region}</span>
                {cs.liveUrl && (
                    <a
                        href={cs.liveUrl}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="case-live-link"
                        className="ml-auto inline-flex items-center gap-1.5 text-cyan-400 hover:text-white transition-colors"
                    >
                        View live
                        <ExternalLink className="w-3 h-3" />
                    </a>
                )}
            </div>
        </header>
    );
}

function Screenshot({ cs }) {
    const ref = useReveal();
    return (
        <section ref={ref} className="reveal relative shell mt-16">
            <div
                className="relative w-full overflow-hidden rounded-2xl"
                style={{
                    border: '1px solid rgba(255,255,255,0.10)',
                    boxShadow: '0 60px 120px -30px rgba(6,182,212,0.30), 0 0 0 1px rgba(255,255,255,0.04) inset',
                    background: 'rgba(11,18,33,0.6)',
                }}
            >
                <img
                    data-testid="case-screenshot"
                    src={cs.image}
                    alt={`${cs.title} — live storefront`}
                    className="w-full h-auto block"
                    loading="lazy"
                />
                {cs.liveUrl && (
                    <a
                        href={cs.liveUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md backdrop-blur-md font-mono text-[10px] tracking-[0.18em] uppercase text-cyan-300 hover:text-white transition-colors"
                        style={{
                            background: 'rgba(5,10,21,0.7)',
                            border: '1px solid rgba(6,182,212,0.30)',
                        }}
                    >
                        {cs.liveUrl.replace(/^https?:\/\//, '')}
                        <ExternalLink className="w-3 h-3" />
                    </a>
                )}
            </div>
        </section>
    );
}

function Lede({ cs }) {
    const ref = useReveal();
    return (
        <section ref={ref} className="reveal relative shell mt-24">
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-start">
                <p className="font-display font-semibold text-[1.7rem] sm:text-[2.1rem] lg:text-[2.6rem] leading-[1.15] tracking-[-0.03em] max-w-3xl text-white">
                    {cs.oneLiner}
                </p>
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-400 font-medium">
                        Built
                    </p>
                    <ul className="mt-4 space-y-2.5">
                        {cs.built.map((b) => (
                            <li key={b} className="flex items-baseline gap-3 text-[15px] text-slate-300">
                                <span className="text-cyan-400 font-mono">+</span>
                                {b}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}

function Sections({ cs }) {
    return (
        <article className="relative shell mt-32 space-y-24">
            {cs.sections.map((s, i) => (
                <SectionBlock key={s.heading} section={s} idx={i} />
            ))}
        </article>
    );
}

function SectionBlock({ section, idx }) {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid={`case-section-${idx}`}
            className="reveal grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 lg:gap-20"
            style={{ transitionDelay: `${idx * 60}ms` }}
        >
            <div className="lg:sticky lg:top-28 self-start">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-cyan-400 font-medium">
                    0{idx + 1}
                </p>
                <h2 className="mt-3 font-display font-semibold text-[28px] md:text-[34px] leading-[1.1] tracking-[-0.03em] text-white">
                    {section.heading}.
                </h2>
            </div>
            <div className="text-[16.5px] leading-[1.7] text-slate-300 max-w-3xl">
                {section.body && (
                    <div className="space-y-5">
                        {section.body.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                )}
                {section.blocks && (
                    <ul className="space-y-7">
                        {section.blocks.map((b) => (
                            <li
                                key={b.h}
                                className="p-5 rounded-xl"
                                style={{
                                    background: 'rgba(255,255,255,0.025)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                }}
                            >
                                <h3 className="font-display font-semibold text-[18px] md:text-[20px] tracking-[-0.025em] text-white">
                                    {b.h}
                                </h3>
                                <p className="mt-3 text-[15px] text-slate-300">{b.b}</p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

function ClosingCTA() {
    const { openSnapshot } = useSnapshot();
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="case-closing-cta"
            className="reveal relative shell mt-20"
        >
            <div
                className="relative rounded-2xl overflow-hidden p-8 sm:p-12 lg:p-16"
                style={{
                    background: 'linear-gradient(160deg, rgba(6,182,212,0.06), rgba(11,18,33,0.7))',
                    border: '1px solid rgba(6,182,212,0.22)',
                    boxShadow: '0 60px 120px -40px rgba(6,182,212,0.35)',
                }}
            >
                <div
                    className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.30), transparent 65%)' }}
                />
                <p className="relative font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-400">
                    §VI — Yours
                </p>
                <h2 className="relative mt-4 font-display font-semibold text-[2.4rem] sm:text-[3.6rem] lg:text-[4.4rem] leading-[1.0] tracking-[-0.045em] max-w-4xl text-white">
                    Want a system{' '}
                    <span className="text-gradient-cyan">like this</span>{' '}
                    for your business?
                </h2>
                <p className="relative mt-7 text-[17px] leading-[1.65] max-w-2xl text-slate-300">
                    Start with a practical snapshot. We will identify the right
                    first system for your business — website, funnel, dashboard,
                    AI workflow, e-commerce flow, or custom app — and how to
                    sequence the build.
                </p>
                <div className="relative mt-10 flex flex-wrap gap-3">
                    <button
                        type="button"
                        onClick={openSnapshot}
                        data-testid="case-cta-snapshot"
                        className="btn btn-primary"
                    >
                        <Sparkles className="w-4 h-4" />
                        Find Where You're Losing Clients
                        <ArrowRight className="w-4 h-4" />
                    </button>
                    <a
                        href="mailto:hello@clearadvance.co?subject=Strategy%20Call"
                        data-testid="case-cta-call"
                        className="btn btn-ghost"
                    >
                        Book a Strategy Call
                    </a>
                </div>
            </div>
        </section>
    );
}

function NextFile({ next }) {
    const ref = useReveal();
    return (
        <section ref={ref} className="reveal relative shell mt-20">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Next file
            </p>
            <Link
                to={`/portfolio/${next.slug}`}
                data-testid="case-next-file"
                className="mt-3 block group rounded-2xl px-7 py-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center transition-all duration-500"
                style={{
                    background: 'rgba(255,255,255,0.025)',
                    border: '1px solid rgba(255,255,255,0.08)',
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.045)';
                    e.currentTarget.style.borderColor = 'rgba(6,182,212,0.30)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.025)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                }}
            >
                <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">
                        {next.category}
                    </p>
                    <h3 className="mt-2 font-display font-semibold text-[1.7rem] md:text-[2.4rem] leading-[1.1] tracking-[-0.035em] text-white">
                        {next.title}
                    </h3>
                </div>
                <span className="font-display font-medium text-[18px] md:text-[20px] text-cyan-400 group-hover:text-white group-hover:translate-x-2 transition-all duration-500 inline-flex items-center gap-2 shrink-0">
                    Open file
                    <ArrowUpRight className="w-5 h-5" />
                </span>
            </Link>
        </section>
    );
}
